import {
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { formatDate, formatDateStr } from '../../../../common-utils/formatters';
import { fetchBodacc, fetchBodaccHistory, resetBodaccHistory } from '../../../store/CoproReducer';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import classNames from '../../../utils/classNames';
import { BodaccDict } from '../../../utils/dictionnayMap';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import Popup from '../../common/Popup';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';
import WarningValidationPending from '../../common/WarningValidationPending';

const headers = [
  { title: 'SIREN' },
  { title: 'Nom SIREN' },
  { title: 'Nom commercial' },
  { title: 'Type d\'alerte' },
  { title: 'Adresse' },
  { title: 'Nom de la copro' },
  { title: 'Gestionnaire' },
  { title: 'Date' },
  { title: 'Voir le détail', class: 'min-w-[120px]' },
];

const sortableColumns = [
  'SIREN', 'Nom SIREN', 'Nom commercial', 'Type d\'alerte', 'Adresse', 'Nom de la copro', 'Gestionnaire', 'Date',
];

interface BodaccEtablissementLinked {
  siret: string;
  idCopro?: number;
  nameCopro?: string;
  numeroVoieEtablissement?: string;
  typeVoieEtablissement?: string;
  libelleVoieEtablissement?: string;
  codePostalEtablissement?: string;
  libelleCommuneEtablissement?: string;
  denominationUniteLegale?: string;
  sexeUniteLegale?: string;
  prenomUsuelUniteLegale?: string;
  nomUniteLegale?: string;
  gestionnaireCopro?: { firstname?: string; lastname?: string };
}

interface BodaccEntry {
  siren: string;
  familleavis?: string;
  familleavisLib?: string;
  commercant: string;
  dateparution: string;
  etablissementLinked: BodaccEtablissementLinked;
}

interface BodaccPersonne {
  denomination?: string;
  formeJuridique?: string;
}

interface BodaccHistoryItem {
  siren: string;
  familleavis?: string;
  publicationavis?: string;
  numeroannonce?: string;
  dateparution?: string;
  ville?: string;
  numerodepartement?: string;
  tribunal?: string;
  numeroVoieEtablissement?: string;
  typeVoieEtablissement?: string;
  libelleVoieEtablissement?: string;
  codePostalEtablissement?: string;
  libelleCommuneEtablissement?: string;
  listepersonnes?: Record<string, BodaccPersonne>;
  jugement?: Record<string, unknown>;
  acte?: Record<string, unknown>;
  modificationsgenerales?: Record<string, unknown>;
}

const labelTypeAvis = {
  radiation: 'Radiation',
  vente: 'Vente',
  collective: 'Procédure collective',
  modification: 'Modification',
  creation: 'Création',
  immatriculation: 'Immatriculation',
};

function BODACC() {
  const dispatch = useAppDispatch();

  const company = useAppSelector((state) => state.auth.company);
  const jwt = useAppSelector((state) => state.auth.token);
  const bodacc = useAppSelector((state) => state.copro.bodacc);
  const bodaccHistory = useAppSelector((state) => state.copro.bodaccHistory);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    if (company && company.id && jwt) {
      dispatch(fetchBodacc());
    }
  }, [company, dispatch, jwt]);

  const handleDetails = (SIRET: string) => {
    setIsPopupOpen(true);
    dispatch(fetchBodaccHistory(SIRET));
  };

  const renderNestedObject = (obj: Record<string, unknown> | object, count = 0) => Object.keys(obj as Record<string, unknown>).map((key, index) => {
    count++;
    const value = obj[key];
    const displayLabel = (BodaccDict[key] ? BodaccDict[key] : key);
        if (typeof value === 'object' && value !== null) {
      return (
        <div key={`bodacc${displayLabel}${count}${index}`} className="ml-4">
          {displayLabel}
          {' '}
          :
          {' '}
          {renderNestedObject(value as Record<string, unknown>, count + 1)}
        </div>
      );
    }
    return (
      <div key={`bodacc${displayLabel}${count}${index}`} className="ml-4">
        {displayLabel}
        {' '}
        :
        {' '}
        {value}
      </div>
    );
  });

  const coloringBodaccText = (jsx: JSX.Element | string, type: string) => {
    // return a span wrapping the string with the right color
    switch (type) {
    case 'Radiation':
      return <span className="text-red-600">{jsx}</span>;
    case 'Vente':
      return <span className="text-amber-500">{jsx}</span>;
    case 'Procédure collective':
      return <span className="text-red-600">{jsx}</span>;
    default:
      return jsx;
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 text-lg">
          <WarningValidationPending />
          <p>
            L'outil "
            <span className="font-bold text-rgpd-blue-light">Commerces et sociétés (surveillance)</span>
            " est essentiel pour les gestionnaires immobiliers. Cette section permet de suivre de près les
            {' '}
            <span className="font-bold text-rgpd-blue-light">défaillances des entreprises</span>
            {' '}
            (BODACC).
            <br />
            {' '}
            Grâce à cette surveillance, vous pouvez mieux anticiper et identifier les entreprises en difficulté pour informer les propriétaires concernés. L'identification proactive des risques potentiels permet une gestion plus efficace et sécurisée de vos actifs immobiliers.
          </p>
        </div>
        <div>
          <div className="w-full overflow-x-scroll">
            <MUITable
              data={bodacc ? (bodacc as unknown as BodaccEntry[]).map((bodaccEntry) => {
                const famBodacc = labelTypeAvis[bodaccEntry.familleavis] || bodaccEntry.familleavis;
                const address = [
                  bodaccEntry.etablissementLinked.numeroVoieEtablissement,
                  bodaccEntry.etablissementLinked.typeVoieEtablissement,
                  bodaccEntry.etablissementLinked.libelleVoieEtablissement,
                  bodaccEntry.etablissementLinked.codePostalEtablissement,
                  bodaccEntry.etablissementLinked.libelleCommuneEtablissement,
                ].join(' ');
                return {
                  SIREN: coloringBodaccText(bodaccEntry.siren, famBodacc),
                  'Nom SIREN': coloringBodaccText(
                    bodaccEntry.etablissementLinked.denominationUniteLegale
                    || ([
                      bodaccEntry.etablissementLinked.sexeUniteLegale === 'M' ? 'MONSIEUR' : 'MADAME',
                      bodaccEntry.etablissementLinked.prenomUsuelUniteLegale,
                      bodaccEntry.etablissementLinked.nomUniteLegale,
                    ].join(' ')), famBodacc),
                  'Nom commercial': coloringBodaccText(bodaccEntry.commercant, famBodacc),
                  'Type d\'alerte': coloringBodaccText(famBodacc, famBodacc),
                  Adresse: <div className="flex items-center">
                    <div>
                      <RedirectionMapButtons address={address} />
                    </div>
                    <span>
                      {coloringBodaccText(
                        address,
                        famBodacc,
                      )}
                    </span>
                  </div>,
                  'Nom de la copro': <div className="flex items-center">
                    <RedirectionCoproButtons coproId={bodaccEntry.etablissementLinked.idCopro} />
                    {coloringBodaccText(bodaccEntry.etablissementLinked.nameCopro, famBodacc)}
                  </div>,
                  Gestionnaire: coloringBodaccText(bodaccEntry.etablissementLinked.gestionnaireCopro.firstname
                    ? `${bodaccEntry.etablissementLinked.gestionnaireCopro.firstname} ${
                      bodaccEntry.etablissementLinked.gestionnaireCopro.lastname ? bodaccEntry.etablissementLinked.gestionnaireCopro.lastname.toUpperCase() : ''}` : '', famBodacc),
                  Date: coloringBodaccText(formatDate(bodaccEntry.dateparution), famBodacc),
                  'Voir le détail': <div className="flex justify-center">
                    <button className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto" onClick={() => handleDetails(bodaccEntry.etablissementLinked.siret)}>
                      Voir le détail
                    </button>
                  </div>,
                };
              }) : []}
              headers={headers}
              tableClass="bg-red-600"
              sortableColumns={sortableColumns}
              isSearchable
              filterColumnName={'Type d\'alerte'}
              selectedFilteredValues={['Radiation', 'Vente', 'Procédure collective']}
            />
            {!bodacc && (
              <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
            )}
            {bodacc && bodacc.length == 0 && (
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucune entreprise en défaillance dans les 6 derniers mois aux adresses de vos copros"
                  className="my-10"
                />
              </div>
            )}

          </div>
          {isPopupOpen && (
            <Popup
              isOpen
              title="Détails BODACC"
              onClose={() => {
                setIsPopupOpen(false);
                dispatch(resetBodaccHistory());
              }}
            >
              {bodaccHistory && bodaccHistory.length > 0 && (
                <div className="max-h-[75dvh] gap-2 overflow-y-scroll">
                  <a
                    key="linkBODACC-site"
                    className="block w-full cursor-pointer rounded-xl bg-rgpd-blue-light p-2 text-center text-sm text-rgpd-white md:inline-block md:w-auto"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.bodacc.fr/pages/annonces-commerciales/?q.registre=registre:${bodaccHistory[0].siren}`}
                  >
                    <GlobeAltIcon className="inline h-4 w-4" />
                    {' '}
                    VOIR SUR LE SITE
                  </a>
                  {(bodaccHistory as unknown as BodaccHistoryItem[]).map((item, idx) => (
                    <div
                      key={`popup-parentbodacc-${idx}`}
                      className={
                        classNames(
                          'p-4 border rounded-lg border-l-4 mt-3',
                          item.familleavis && item.familleavis == 'vente' && 'border-amber-300',
                          item.familleavis && item.familleavis == 'radiation' && 'border-red-500',
                          item.familleavis && item.familleavis == 'collective' && 'border-red-500',
                        )
                      }
                    >
                      <h3 className="font-bold">
                        Jugement{' '}
                        {labelTypeAvis[item.familleavis]}
                      </h3>
                      <div key={`popup-details-bodacc-${idx}`}>
                        Annonce
                        {' '}
                        {item.publicationavis}
                        /N°
                        {item.numeroannonce}
                        {' '}
                        publiée le
                        {' '}
                        {formatDateStr(item.dateparution)}
                        <br />
                        RCS :
                        {' '}
                        {item.siren}
                        {' '}
                        -
                        {' '}
                        {item.ville}
                        {' '}
                        - Département :
                        {' '}
                        {item.numerodepartement}
                        <br />
                        Annonce déposée au
                        {' '}
                        {item.tribunal}
                        <br />
                        {item.listepersonnes && (
                          <>
                            Personnes :
                            {' '}
                            <br />
                            {item.listepersonnes && Object.values(item.listepersonnes).map((personne: BodaccPersonne, personIndex: number) => (
                              <div key={`person-${personne.denomination}-${personIndex}`} className="ml-4">
                                Dénomination :
                                {' '}
                                {personne.denomination}
                                <br />
                                Forme juridique :
                                {' '}
                                {personne.formeJuridique}
                                <br />
                              </div>
                            ))}
                          </>
                        )}
                        Adresse de la copro concernée :
                        {' '}
                        {item.numeroVoieEtablissement}
                        {' '}
                        {item.typeVoieEtablissement}
                        {' '}
                        {item.libelleVoieEtablissement}
                        {' '}
                        {item.codePostalEtablissement}
                        {' '}
                        {item.libelleCommuneEtablissement}
                        <br />
                        {item.jugement && (
                          <div className="mt-2 border border-dashed border-slate-600 p-2">
                            <b>Jugement prononçant : </b>
                            <br />
                            {renderNestedObject(item.jugement)}
                          </div>
                        )}
                        {item.acte && (
                          <div className="mt-2 border border-dashed border-slate-600 p-2">
                            <b>Acte effectué : </b>
                            <br />
                            {renderNestedObject(item.acte)}
                          </div>
                        )}
                        {item.modificationsgenerales && (
                          <div className="mt-2 border border-dashed border-slate-600 p-2">
                            <b>Modifications générales :</b>
                            {' '}
                            <br />
                            {renderNestedObject(item.modificationsgenerales)}
                          </div>
                        )}
                      </div>
                      {}
                    </div>
                  ))}
                </div>
              )}
              {!bodaccHistory && (
                <Skeleton count={10} height={25} width={400} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
              )}
            </Popup>
          )}
        </div>
      </div>
    </div>
  );
}

export default BODACC;
